import React, { useState } from "react";
import { Calendar, MapPin, Clock, MessagesSquare, Scale, Mic, Users, PartyPopper, Presentation, Network } from "lucide-react";
import { UPCOMING_EVENTS, PAST_EVENTS, EVENT_TYPES } from "../mock";

const EVENT_ICONS = {
  Discussions: MessagesSquare,
  "Interactive Forums": Users,
  Socials: PartyPopper,
  "Expert Panels": Presentation,
  Debates: Scale,
  Speakers: Mic,
  "Networking Events": Network,
};

export default function Events() {
  const [filter, setFilter] = useState("past");
  const filtered = filter === "upcoming" ? UPCOMING_EVENTS : PAST_EVENTS;

  return (
    <div>
      <section className="relative pt-16 sm:pt-24 pb-8 sm:pb-14 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-6">Events</p>
          <h1 className="font-serif-display text-[56px] md:text-[96px] font-bold leading-[0.98] text-white tracking-tight">
            What <span className="hl-block-deep">we do.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-slate-200 font-serif-body text-lg leading-relaxed">
            At BridgeColumbia, we seek to build a community in which constructive disagreement and open discussion can thrive. We host a series of events meant to strengthen the Bridge community and introduce our members to diverse perspectives.
          </p>
        </div>
      </section>

      {/* Event types */}
      <section className="py-8 sm:py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-6">Types of Events</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {EVENT_TYPES.map((t, i) => {
              const Icon = EVENT_ICONS[t.title] || MessagesSquare;
              return (
                <article key={i} className="rounded-2xl border border-white/15 bg-[#145074] p-7 hover:border-cyan-300/60 hover:bg-[#1a618d] transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-cyan-300/15 border border-cyan-300/40 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-cyan-200" />
                  </div>
                  <h3 className="mt-5 font-serif-display text-2xl text-white">{t.title}</h3>
                  <p className="mt-3 text-slate-200 font-serif-body leading-relaxed text-[15px]">{t.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Upcoming / Past */}
      <section className="py-8 sm:py-14 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
            <div className="inline-flex rounded-full border border-white/15 bg-[#145074] p-1">
              {[
                { k: "past", label: "Notable" },
                { k: "upcoming", label: "Upcoming" },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  className={`px-5 py-2 rounded-full text-sm transition-colors ${
                    filter === t.k
                      ? "bg-cyan-300 text-[#062a40] font-medium"
                      : "text-slate-200 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-300 font-serif-body italic">
              Fall 2026 programming will be announced soon.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-3 top-2 bottom-2 w-px bg-white/15 hidden md:block" />
            <div className="space-y-6">
              {filtered.length === 0 && (
                <div className="rounded-2xl border border-white/15 bg-[#145074] p-8 md:p-10 text-center">
                  <p className="font-serif-display text-2xl text-white">No upcoming events scheduled.</p>
                  <p className="mt-3 text-slate-300 max-w-lg mx-auto font-serif-body">
                    New programming for the fall term will be posted here soon. In the meantime, follow us on Instagram or apply for the fellowship to be the first to hear.
                  </p>
                </div>
              )}
              {filtered.map((e, i) => (
                <article
                  key={i}
                  className="relative md:pl-12 rounded-2xl border border-white/15 bg-[#145074] p-7 md:p-8 hover:border-cyan-300/60 hover:bg-[#1a618d] transition-colors"
                >
                  <div className="hidden md:block absolute left-[-6px] top-10 w-3.5 h-3.5 rounded-full bg-cyan-300 shadow-[0_0_0_4px_rgba(9,51,73,1)]" />
                  <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-cyan-200">
                    <Calendar className="w-3.5 h-3.5" /> {e.date}
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-300 normal-case tracking-normal text-[13px]">{e.host}</span>
                  </div>
                  <h3 className="mt-5 font-serif-display text-3xl md:text-4xl text-white leading-tight">
                    {e.title}
                  </h3>
                  {e.speakers && (
                    <p className="mt-2 text-slate-200 font-serif-body italic">{e.speakers}</p>
                  )}
                  <p className="mt-4 text-slate-200 leading-relaxed max-w-3xl font-serif-body">{e.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-5 text-sm text-slate-100">
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-cyan-200" /> {e.time}</span>
                    <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cyan-200" /> {e.location}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
